import React, { useState } from "react"
import { Link } from "react-router-dom"
import { Facebook, Instagram, Linkedin, Globe, Award, GraduationCap, Mountain, Plane } from "lucide-react"
import { useFeatureFlag } from "@/hooks/useFeatureFlag"
import { toast } from "sonner"

const Footer = () => {
  const { data: awardsPageEnabled } = useFeatureFlag('awards_page')
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) {
      toast.error('Please enter your email')
      return
    }
    
    setIsSubmitting(true)
    // Simulate newsletter subscription
    await new Promise(resolve => setTimeout(resolve, 500))
    toast.success('Thanks for subscribing!')
    setEmail('')
    setIsSubmitting(false)
  }
  
  return (
    <footer className="bg-secondary text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Events */}
          <div>
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Events
            </h3>
            <div className="space-y-2 text-sm">
              <Link to="/events?type=festival" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
                <Mountain className="h-4 w-4" />
                Festivals
              </Link>
              <Link to="/events?type=academy" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
                <GraduationCap className="h-4 w-4" />
                Academies
              </Link>
              <Link to="/events?type=retreat" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
                <Mountain className="h-4 w-4" />
                Retreats
              </Link>
              <Link to="/events?type=workation" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
                <Plane className="h-4 w-4" />
                Workations
              </Link>
              {awardsPageEnabled && (
                <Link to="/awards" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
                  <Award className="h-4 w-4" />
                  Awards
                </Link>
              )}
            </div>
          </div>

          {/* Community & Resources */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Community</h3>
            <div className="space-y-2 text-sm">
              <Link to="/community" className="block text-white/80 hover:text-white transition-colors">
                Community Hub
              </Link>
              <Link to="/submit-event" className="block text-white/80 hover:text-white transition-colors">
                Submit Event
              </Link>
              <Link to="/blog" className="block text-white/80 hover:text-white transition-colors">
                Blog
              </Link>
              <Link to="/partners" className="block text-white/80 hover:text-white transition-colors">
                Partners
              </Link>
              <Link to="/contact" className="block text-white/80 hover:text-white transition-colors">
                Contact Us
              </Link>
            </div>
          </div>

          {/* Join Us & Jobs */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Join Us & Jobs</h3>
            <div className="space-y-2 text-sm">
              <a href="https://nomad-job.com" target="_blank" rel="noopener noreferrer" className="block text-white/80 hover:text-white transition-colors">
                Community Manager
              </a>
              <a href="https://nomad-job.com" target="_blank" rel="noopener noreferrer" className="block text-white/80 hover:text-white transition-colors">
                Volunteer
              </a>
              <a href="https://nomad-job.com" target="_blank" rel="noopener noreferrer" className="block text-white/80 hover:text-white transition-colors">
                Young Talent
              </a>
              <a href="https://nomad-job.com" target="_blank" rel="noopener noreferrer" className="block text-white/80 hover:text-white transition-colors">
                Remote Jobs
              </a>
              <a href="https://nomad-job.com" target="_blank" rel="noopener noreferrer" className="block text-white/80 hover:text-white transition-colors">
                On-Site Jobs
              </a>
            </div>
          </div>

          {/* About & Legal */}
          <div>
            <h3 className="font-semibold text-lg mb-4">About</h3>
            <div className="space-y-2 text-sm text-white/80">
              <p className="font-semibold text-white">Nomad-fest.com</p>
              <p>Rue de la Dranse 2</p>
              <p>1945 Liddes</p>
              <p>Switzerland</p>
              <div className="space-y-2 mt-4 pt-4 border-t border-white/20">
                <Link to="/privacy" className="block hover:text-white transition-colors">
                  Privacy Policy
                </Link>
                <Link to="/terms" className="block hover:text-white transition-colors">
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>

          {/* Follow Us */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Follow Us</h3>
            <div className="flex flex-wrap gap-3">
              <a href="https://www.instagram.com/nomad_fest_community" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors" aria-label="Instagram">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="https://www.linkedin.com/company/coliving-frilingue" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors" aria-label="LinkedIn">
                <Linkedin className="h-5 w-5" />
              </a>
              <a href="https://www.facebook.com/coliving.frilingue.community" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors" aria-label="Facebook">
                <Facebook className="h-5 w-5" />
              </a>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="mt-6 pt-6 border-t border-white/20">
              <p className="text-sm text-white/80 mb-2">Newsletter</p>
              <div className="flex gap-2">
                <input 
                  type="email" 
                  placeholder="Your email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded-md text-sm text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? '...' : 'Join'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Bottom section */}
        <div className="border-t border-white/20 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center space-x-2 mb-4 md:mb-0">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">NF</span>
            </div>
            <span className="font-bold text-xl">nomad-fest.com</span>
          </div>
          <p className="text-sm text-white/60">© 2026 Nomad Fest</p>
        </div>
      </div>
    </footer>
  )
}

export { Footer }